/**
 * Stock Chart Viewer - Chart Initialization
 */

import { createChart, CrosshairMode, PriceScaleMode } from 'lightweight-charts';
import { state } from '../state.js';
import { CHART_COLORS } from '../config.js';
import { elements } from '../ui/dom.js';
import { calculateHeikinAshi } from './indicators.js';
import { addHorizontalLine, autoFibonacci, getDrawingMode } from './drawing.js';

export function updateChartType(type) {
    if (!state.chart || !state.candleSeries) return;

    // Remove existing main series
    state.chart.removeSeries(state.candleSeries);

    // Create new series based on type
    if (type === 'line') {
        state.candleSeries = state.chart.addLineSeries({
            color: CHART_COLORS.upColor, lineWidth: 2
        });
    } else if (type === 'area') {
        state.candleSeries = state.chart.addAreaSeries({
            topColor: 'rgba(33, 150, 243, 0.56)',
            bottomColor: 'rgba(33, 150, 243, 0.04)',
            lineColor: 'rgba(33, 150, 243, 1)',
            lineWidth: 2,
        });
    } else {
        // Candle or Heikin Ashi (both use candlestick series structure)
        state.candleSeries = state.chart.addCandlestickSeries({
            upColor: CHART_COLORS.upColor,
            downColor: CHART_COLORS.downColor,
            borderUpColor: CHART_COLORS.upColor,
            borderDownColor: CHART_COLORS.downColor,
            wickUpColor: CHART_COLORS.upColor,
            wickDownColor: CHART_COLORS.downColor,
        });
    }

    // Set Data
    if (state.stockData && state.stockData.candleData) {
        let dataToSet = state.stockData.candleData;

        if (type === 'heikin') {
            dataToSet = calculateHeikinAshi(dataToSet);
        } else if (type === 'line' || type === 'area') {
            // Line/Area needs { time, value }
            dataToSet = dataToSet.map(d => ({ time: d.time, value: d.close }));
        }

        state.candleSeries.setData(dataToSet);
    }
}

// Global listener for chart type modification
document.addEventListener('chart-type-change', (e) => {
    updateChartType(e.detail.type);
});

export function initChart() {
    const container = elements.chartContainer;

    // ============================================
    // 1. Initialize Main Chart
    // ============================================
    if (state.chart) {
        state.chart.remove();
        state.chart = null;
    }

    state.chart = createChart(container, {
        width: container.clientWidth,
        height: 400,
        layout: {
            background: { type: 'solid', color: CHART_COLORS.background },
            textColor: CHART_COLORS.text,
        },
        grid: {
            vertLines: { color: CHART_COLORS.grid },
            horzLines: { color: CHART_COLORS.grid },
        },
        crosshair: {
            mode: CrosshairMode.Normal,
            vertLine: {
                width: 1,
                color: CHART_COLORS.crosshair,
                style: 3, // Dashed
                labelBackgroundColor: CHART_COLORS.crosshair,
            },
            horzLine: {
                width: 1,
                color: CHART_COLORS.crosshair,
                style: 3,
                labelBackgroundColor: CHART_COLORS.crosshair,
            },
        },
        rightPriceScale: { borderColor: CHART_COLORS.grid },
        timeScale: {
            borderColor: CHART_COLORS.grid,
            timeVisible: true,
            secondsVisible: false,
        },
    });

    state.candleSeries = state.chart.addCandlestickSeries({
        upColor: CHART_COLORS.upColor,
        downColor: CHART_COLORS.downColor,
        borderUpColor: CHART_COLORS.upColor,
        borderDownColor: CHART_COLORS.downColor,
        wickUpColor: CHART_COLORS.upColor,
        wickDownColor: CHART_COLORS.downColor,
    });

    state.volumeSeries = state.chart.addHistogramSeries({
        color: CHART_COLORS.volumeUp,
        priceFormat: { type: 'volume' },
        priceScaleId: '',
    });

    state.volumeSeries.priceScale().applyOptions({
        scaleMargins: { top: 0.8, bottom: 0 },
    });

    // SMA Series
    state.sma20Series = state.chart.addLineSeries({
        color: '#ffc107', lineWidth: 2, priceLineVisible: false, lastValueVisible: false,
    });
    state.sma50Series = state.chart.addLineSeries({
        color: '#9c27b0', lineWidth: 2, priceLineVisible: false, lastValueVisible: false,
    });

    // Bollinger Bands Series (Overlay)
    state.bbUpperSeries = state.chart.addLineSeries({
        color: 'rgba(63, 185, 80, 0.5)', lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
    });
    state.bbLowerSeries = state.chart.addLineSeries({
        color: 'rgba(63, 185, 80, 0.5)', lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
    });

    // Clear any previous comparison series on full re-init
    state.comparisonSeries = [];


    // ============================================
    // 2. Initialize RSI Chart (Hidden by default)
    // ============================================
    if (state.rsiChart) {
        state.rsiChart.remove();
        state.rsiChart = null;
    }

    const rsiContainer = elements.rsiChartContainer;
    state.rsiChart = createChart(rsiContainer, {
        width: rsiContainer.clientWidth,
        height: 150,
        layout: {
            background: { type: 'solid', color: CHART_COLORS.background },
            textColor: CHART_COLORS.text,
        },
        grid: {
            vertLines: { color: CHART_COLORS.grid },
            horzLines: { color: CHART_COLORS.grid },
        },
        timeScale: { visible: false }, // Hide time axis for stacked chart
        rightPriceScale: { borderColor: CHART_COLORS.grid },
    });

    state.rsiSeries = state.rsiChart.addLineSeries({
        color: '#e91e63', lineWidth: 2,
        priceScaleId: 'right',
    });

    // RSI Reference Lines (70/30)
    const rsi70 = state.rsiChart.addLineSeries({
        color: '#ffffff', lineWidth: 1, lineStyle: 2, lastValueVisible: false, priceLineVisible: false
    });
    const rsi30 = state.rsiChart.addLineSeries({
        color: '#ffffff', lineWidth: 1, lineStyle: 2, lastValueVisible: false, priceLineVisible: false
    });
    // Can't easily set constant data without real time points, 
    // so we will just handle them as part of updates or overlay logic usually.
    // For simplicity in lightweight-charts, we often just draw a line across data points.
    // Or we use createPriceLine if we want horizontal static lines, but that's per-series.
    // Let's add them to rsiSeries as price lines.
    state.rsiSeries.createPriceLine({ price: 70, color: '#ffffff', lineWidth: 1, lineStyle: 2, axisLabelVisible: false });
    state.rsiSeries.createPriceLine({ price: 30, color: '#ffffff', lineWidth: 1, lineStyle: 2, axisLabelVisible: false });

    // ============================================
    // 3. Initialize MACD Chart (Hidden by default)
    // ============================================
    if (state.macdChart) {
        state.macdChart.remove();
        state.macdChart = null;
    }

    const macdContainer = elements.macdChartContainer;
    state.macdChart = createChart(macdContainer, {
        width: macdContainer.clientWidth,
        height: 150,
        layout: {
            background: { type: 'solid', color: CHART_COLORS.background },
            textColor: CHART_COLORS.text,
        },
        grid: {
            vertLines: { color: CHART_COLORS.grid },
            horzLines: { color: CHART_COLORS.grid },
        },
        timeScale: { visible: false },
        rightPriceScale: { borderColor: CHART_COLORS.grid },
    });

    state.macdHistSeries = state.macdChart.addHistogramSeries({
        color: '#26a69a',
    });
    state.macdSeries = state.macdChart.addLineSeries({
        color: '#2962ff', lineWidth: 2,
    });
    state.macdSignalSeries = state.macdChart.addLineSeries({
        color: '#ff6d00', lineWidth: 2,
    });

    // ============================================
    // 4. Synchronization
    // ============================================
    const mainTimeScale = state.chart.timeScale();
    const rsiTimeScale = state.rsiChart ? state.rsiChart.timeScale() : null;
    const macdTimeScale = state.macdChart ? state.macdChart.timeScale() : null;
    const stochTimeScale = state.stochChart ? state.stochChart.timeScale() : null;

    function syncTo(source, targets) {
        source.subscribeVisibleTimeRangeChange(range => {
            if (range) {
                targets.forEach(t => {
                    if (t && typeof t.setVisibleTimeRange === 'function') {
                        t.setVisibleTimeRange(range);
                    }
                });
            }
        });
    }

    // Main drives others
    syncTo(mainTimeScale, [rsiTimeScale, macdTimeScale, stochTimeScale]);
    // Allow others to drive main (bi-directional sync)
    syncTo(rsiTimeScale, [mainTimeScale, macdTimeScale, stochTimeScale]);
    syncTo(macdTimeScale, [mainTimeScale, rsiTimeScale, stochTimeScale]);
    if (stochTimeScale) {
        syncTo(stochTimeScale, [mainTimeScale, rsiTimeScale, macdTimeScale]);
    }

    // ============================================
    // 5. Resize Observer
    // ============================================
    const resizeObserver = new ResizeObserver(entries => {
        // We need to resize all active charts
        if (state.chart) {
            // Main chart container might change size
            const rect = elements.chartContainer.getBoundingClientRect();
            // Note: getBoundingClientRect includes padding, clientWidth excludes it.
            // We need to match the container width.
            const width = elements.chartContainer.clientWidth;

            state.chart.applyOptions({ width: width });
            state.rsiChart.applyOptions({ width: width });
            state.macdChart.applyOptions({ width: width });
            if (state.stochChart) state.stochChart.applyOptions({ width: width });
        }
    });
    resizeObserver.observe(container);

    // Click Handler for Drawings
    state.chart.subscribeClick(param => {
        if (!state.isDrawingMode || !param.point || !state.candleSeries) return;

        const price = state.candleSeries.coordinateToPrice(param.point.y);
        if (price === null) return;

        const mode = getDrawingMode();

        if (mode === 'line') {
            addHorizontalLine(price);
        } else if (mode === 'fib') {
            // For now, auto-fib on click logic or just trigger it. 
            // Ideally fib is drawn based on visible range high/low. 
            // Let's just trigger autoFibonacci() regardless of click position for MVP
            autoFibonacci();
        }
    });
}

export function clearDrawings() {
    if (state.candleSeries && state.drawings.length > 0) {
        state.drawings.forEach(line => state.candleSeries.removePriceLine(line));
        state.drawings = [];
    }
}


export function clearChart() {
    if (state.chart) {
        state.candleSeries.setData([]);
        state.volumeSeries.setData([]);
        state.sma20Series.setData([]);
        state.sma50Series.setData([]);
        state.bbUpperSeries.setData([]);
        state.bbLowerSeries.setData([]);

        // Clear comparisons
        state.comparisonSeries.forEach(s => state.chart.removeSeries(s.series));
        state.comparisonSeries = [];
    }
    if (state.rsiChart) {
        state.rsiSeries.setData([]);
    }
    if (state.macdChart) {
        state.macdSeries.setData([]);
        state.macdSignalSeries.setData([]);
        state.macdHistSeries.setData([]);
    }
    if (state.stochChart) {
        state.stochSeries.k.setData([]);
        state.stochSeries.d.setData([]);
    }
}

// ============================================
// Phase 2: Comparison Logic
// ============================================

const COMPARE_COLORS = ['#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#00bcd4', '#009688', '#ff9800', '#795548'];

export function addComparison(symbol, data) {
    if (!state.chart) return;

    // Pick color
    const colorIndex = state.comparisonSeries.length % COMPARE_COLORS.length;
    const color = COMPARE_COLORS[colorIndex];

    const series = state.chart.addLineSeries({
        color: color,
        lineWidth: 2,
        priceScaleId: 'right', // Share scale
        title: symbol,
    });

    // Map data to LineData { time, value }
    const lineData = data.map(d => ({ time: d.time, value: d.close }));
    series.setData(lineData);

    state.comparisonSeries.push({ symbol, series, color });

    updatePriceScaleMode();
}

// Listeners
document.addEventListener('add-comparison', (e) => {
    addComparison(e.detail.symbol, e.detail.data);
});

document.addEventListener('clear-drawings', () => {
    clearDrawings();
});



export function removeComparison(symbol) {
    const index = state.comparisonSeries.findIndex(s => s.symbol === symbol);
    if (index !== -1) {
        state.chart.removeSeries(state.comparisonSeries[index].series);
        state.comparisonSeries.splice(index, 1);
        updatePriceScaleMode();
    }
}

function updatePriceScaleMode() {
    if (!state.chart || !state.candleSeries) return;

    // If we have comparisons, use Percentage Mode
    const mode = state.comparisonSeries.length > 0 ? PriceScaleMode.Percentage : PriceScaleMode.Normal;

    state.chart.priceScale('right').applyOptions({
        mode: mode
    });
}

